import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockIcon from '@mui/icons-material/Lock';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import AuthUtil from '../../redux/AuthUtil';
import { STRIPE as STRIPE_ENDPOINTS } from '../global/Server/endpoints';

// Same animated success-panel keyframes as SavedCardPaymentDialog. Two
// effects: green check pops in with a slight overshoot for tactile
// feedback, then a soft radial halo continues pulsing so the screen
// feels alive after settling. The slide-up animation staggers the
// text rows in after the bubble lands.
const SUCCESS_KEYFRAMES = `
@keyframes nubeSuccessPop {
  0%   { transform: scale(0.4); opacity: 0; }
  55%  { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes nubeSuccessRipple {
  0%   { transform: scale(1);   opacity: 0.55; }
  100% { transform: scale(1.6); opacity: 0; }
}
@keyframes nubeSuccessSlideUp {
  0%   { transform: translateY(10px); opacity: 0; }
  100% { transform: translateY(0);    opacity: 1; }
}
`;

/**
 * Phase 2 universal Stripe dialog. Two modes:
 *
 *   mode='save_card'        — SetupIntent. No charge. Attaches the card to
 *                              the Stripe Customer for off-session auto-renewal.
 *                              Used in Profile → Payment Methods → Add Card.
 *
 *   mode='pay_and_activate' — PaymentIntent. Charges for the next cycle.
 *                              Used by the Login expired dialog and Profile's
 *                              "Pay Now". When `serialKey` is supplied we hit
 *                              the permitAll payAndActivateFromKey endpoint
 *                              for the locked-out flow; otherwise we use the
 *                              authenticated createPayNow endpoint.
 *
 * Enterprise-grade guarantees baked in:
 *   1. ONE charge per cycle. clientSecret is cached on first open and
 *      reused if the dialog is closed/reopened mid-flow. Backend also
 *      passes Stripe Idempotency-Key so even a server-side race would
 *      collapse to a single PaymentIntent.
 *   2. Button is hard-disabled while a confirmPayment is in-flight. The
 *      handler itself short-circuits if `submitting` is already true so
 *      repeated synthetic events do nothing.
 *   3. Stripe / network errors surface clean messages and KEEP the PI
 *      alive so retry is one click. No partial state is written to our
 *      backend until Stripe returns succeeded — webhooks are the only
 *      path that creates LicensePayment rows.
 *   4. Activation status is polled after a successful charge until the
 *      webhook has reactivated the company. onSuccess fires only after
 *      activation, so the parent's auto-login etc. doesn't race ahead.
 */
class PayWithStripeDialog extends React.Component {
  state = {
    // Resolved publishable key + Stripe SDK promise. Done in the dialog
    // (rather than at app root) so the dialog stays self-contained and
    // works even if a StripeProvider isn't mounted above.
    stripePromise: null,
    publishableKey: null,

    // The active intent. clientSecret is the only secret we receive; the
    // PaymentElement uses it to confirm with Stripe directly.
    clientSecret: null,
    paymentIntentId: null,
    setupIntentId: null,
    amountUSD: null,
    // Fee pass-through breakdown for the payment summary. amountUSD is the
    // grossed-up total the customer pays (== totalUSD); baseUSD is the plan
    // price; feeUSD is Stripe's processing fee the customer covers.
    baseUSD: null,
    feeUSD: null,
    totalUSD: null,
    companyId: null,

    // Lifecycle flags. `loading` = waiting on initial fetch. `submitting`
    // = confirmPayment in-flight. `awaitingActivation` = charge succeeded,
    // polling for the webhook to land. `done` = activated, will fire
    // onSuccess. `error` = surfaced message; PI stays alive.
    loading: true,
    submitting: false,
    awaitingActivation: false,
    done: false,
    error: null,
  };

  _mounted = false;
  _pollTimer = null;

  componentDidMount() {
    this._mounted = true;
    // Bootstrap (publicConfig + intent create) only when the dialog is
    // actually open. Callers commonly render this component unconditionally
    // with `open={someFlag}`, so without this gate an unauthenticated page
    // (e.g. Login) would fire /stripe/createPayNow on mount, hit 401, and
    // trigger AuthUtil's auto-logout — an infinite refresh loop.
    if (this.props.open) this.bootstrap();
  }

  componentDidUpdate(prevProps) {
    // Trigger the bootstrap on open transitions, and reset state on close
    // so the next open starts fresh (clientSecret, error, done flags all
    // get cleared). The PaymentIntent itself stays alive on Stripe's side
    // for retries; we re-fetch its details on the next open.
    if (!prevProps.open && this.props.open) {
      this.setSafe({
        loading: true,
        stripePromise: null,
        publishableKey: null,
        clientSecret: null,
        paymentIntentId: null,
        setupIntentId: null,
        amountUSD: null,
        companyId: null,
        submitting: false,
        awaitingActivation: false,
        done: false,
        error: null,
      });
      this.bootstrap();
    } else if (prevProps.open && !this.props.open) {
      if (this._pollTimer) {
        clearTimeout(this._pollTimer);
        this._pollTimer = null;
      }
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._pollTimer) clearTimeout(this._pollTimer);
  }

  // Fetches publishable key + initial intent in parallel. Errors are
  // surfaced through the dialog body — we never throw to the parent.
  bootstrap = async () => {
    try {
      // 1) Publishable key. Cached on the window object via loadStripe
      //    internals, so calling this on every open is cheap.
      const configRes = await AuthUtil.getRequest(
        STRIPE_ENDPOINTS.PUBLIC_CONFIG
      );
      if (!configRes?.data?.success || !configRes.data.publishableKey) {
        this.setSafe({
          loading: false,
          error:
            configRes?.data?.message ||
            'Stripe is not configured. Please contact your administrator.',
        });
        return;
      }
      const publishableKey = configRes.data.publishableKey;
      const stripePromise = loadStripe(publishableKey);

      // 2) Intent. Mode dictates which endpoint.
      const intent = await this.createIntent();
      if (!intent || !intent.clientSecret) {
        this.setSafe({
          loading: false,
          stripePromise,
          publishableKey,
          error: intent?.message || 'Could not initialize payment.',
        });
        return;
      }

      this.setSafe({
        loading: false,
        stripePromise,
        publishableKey,
        clientSecret: intent.clientSecret,
        paymentIntentId: intent.paymentIntentId,
        setupIntentId: intent.setupIntentId,
        amountUSD: intent.amountUSD,
        baseUSD: intent.baseUSD != null ? intent.baseUSD : null,
        feeUSD: intent.feeUSD != null ? intent.feeUSD : null,
        totalUSD: intent.totalUSD != null ? intent.totalUSD : intent.amountUSD,
        companyId: intent.companyId || this.props.companyId,
      });
    } catch (e) {
      this.setSafe({
        loading: false,
        error: 'Could not start the payment flow. Please try again.',
      });
    }
  };

  createIntent = async () => {
    const { mode, serialKey, companyId, paymentMethodId, amount, paymentId } =
      this.props;

    if (mode === 'save_card') {
      const res = await AuthUtil.postRequest(
        STRIPE_ENDPOINTS.CREATE_SETUP_INTENT,
        {
          companyId,
        }
      );
      return res?.data;
    }

    // pay_and_activate
    if (serialKey) {
      const res = await AuthUtil.postRequest(
        STRIPE_ENDPOINTS.PAY_AND_ACTIVATE_FROM_KEY,
        {
          serialKey,
          amount,
        }
      );
      return res?.data;
    }
    // paymentId anchors the PaymentIntent to the SPECIFIC UNPAID row the
    // admin clicked (April vs. May vs. June). Without it the backend
    // resolves the cycle via nextCycleEndFor(company), which anchors on
    // the active license's expiry — so clicking April with a 5/15-expiry
    // key would silently credit June. Threading it through keeps the
    // payment landing on the row the user actually picked.
    const res = await AuthUtil.postRequest(STRIPE_ENDPOINTS.CREATE_PAY_NOW, {
      companyId,
      paymentMethodId,
      amount,
      paymentId,
    });
    return res?.data;
  };

  setSafe = (next) => {
    if (this._mounted) this.setState(next);
  };

  // After a successful confirmPayment, poll the server until the webhook
  // (or payNowStatus self-heal) has materialized the LicensePayment row
  // and activated the company. We hit a permitAll endpoint so the
  // locked-out flow works without a token.
  //
  // Cadence is front-loaded: first call fires immediately, then 500ms,
  // 1s, 1.5s, then 2s steady state. In dev without `stripe listen` the
  // self-heal does Stripe round-trips inside the request, so the first
  // tick can take a few seconds — packing the retries earlier shaves
  // perceived "Activating..." time without spamming the server.
  startActivationPoll = () => {
    let attempts = 0;
    const delays = [500, 1000, 1500];
    const tick = async () => {
      if (!this._mounted) return;
      attempts += 1;
      try {
        const res = await AuthUtil.postRequest(
          STRIPE_ENDPOINTS.PAY_NOW_STATUS,
          {
            paymentIntentId: this.state.paymentIntentId,
            // Locked-out flow has no Bearer token — the serial key is the
            // co-secret the server uses to authorize reading this status.
            serialKey: this.props.serialKey,
          }
        );
        if (res?.data?.success && res.data.activated) {
          this.setSafe({ awaitingActivation: false, done: true });
          if (this.props.onSuccess) {
            this.props.onSuccess({
              paymentIntentId: this.state.paymentIntentId,
              companyId: res.data.companyId,
              cycleEnd: res.data.cycleEnd,
            });
          }
          return;
        }
        if (
          res?.data?.success &&
          res.data.settled &&
          this.props.mode === 'pay_and_activate' &&
          !this.props.requiresActivation
        ) {
          // For Profile pay-now where the company is already active, "settled"
          // alone is the success signal — no activation needed.
          this.setSafe({ awaitingActivation: false, done: true });
          if (this.props.onSuccess) {
            this.props.onSuccess({
              paymentIntentId: this.state.paymentIntentId,
              companyId: res.data.companyId,
              cycleEnd: res.data.cycleEnd,
            });
          }
          return;
        }
      } catch (e) {
        // Transient — keep polling.
      }
      if (attempts >= 30) {
        // Give up but don't error — the payment did succeed, the company
        // will activate within seconds of the webhook landing. Direct the
        // user to refresh.
        this.setSafe({
          awaitingActivation: false,
          done: true,
          error:
            'Payment succeeded. Activation is finalizing — please refresh in a few seconds.',
        });
        if (this.props.onSuccess) {
          this.props.onSuccess({
            paymentIntentId: this.state.paymentIntentId,
            pending: true,
          });
        }
        return;
      }
      // delays[0..2] = 500, 1000, 1500 — those map to the gap BEFORE the
      // 2nd, 3rd, 4th tick. Everything from tick #5 onward uses the
      // steady 2000 ms cadence.
      const delay = delays[attempts - 1] != null ? delays[attempts - 1] : 2000;
      this._pollTimer = setTimeout(tick, delay);
    };
    tick();
  };

  // Plan + Stripe processing fee + total, shown above the PaymentElement so
  // the customer sees exactly what they'll be charged before confirming. The
  // grossed-up total is also what Apple Pay / Google Pay display, because the
  // PaymentIntent amount IS the total. Falls back gracefully when the fee
  // breakdown isn't present (e.g. fee pass-through disabled server-side):
  // only the total row renders.
  renderPaymentSummary = () => {
    const { baseUSD, feeUSD, totalUSD } = this.state;
    const money = (v) => `$${Number(v || 0).toFixed(2)}`;
    const hasFee = feeUSD != null && Number(feeUSD) > 0 && baseUSD != null;
    return (
      <Box
        mb={2}
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: '12px 14px',
        }}
      >
        {hasFee && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{ marginBottom: 6 }}
            >
              <Typography style={{ fontSize: 13, color: '#475569' }}>
                Plan
              </Typography>
              <Typography
                style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}
              >
                {money(baseUSD)}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{ marginBottom: 8 }}
            >
              <Typography style={{ fontSize: 13, color: '#475569' }}>
                Processing fee
              </Typography>
              <Typography
                style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}
              >
                {money(feeUSD)}
              </Typography>
            </Box>
            <Box
              style={{
                borderTop: '1px solid #e2e8f0',
                paddingTop: 8,
              }}
            />
          </>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}
          >
            Total
          </Typography>
          <Typography
            style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}
          >
            {money(totalUSD != null ? totalUSD : this.state.amountUSD)}
          </Typography>
        </Box>
      </Box>
    );
  };

  render() {
    const { open, onClose, mode } = this.props;
    const {
      loading,
      stripePromise,
      clientSecret,
      error,
      done,
      awaitingActivation,
    } = this.state;

    return (
      <Dialog
        open={!!open}
        onClose={() => {
          // Only allow closing when nothing is mid-flight. Prevents accidental
          // dismissal during the brief window between confirmPayment and webhook.
          if (this.state.submitting || this.state.awaitingActivation) return;
          onClose && onClose();
        }}
        maxWidth="sm"
        fullWidth
        // Stripe Link's "Confirm it's you" verification opens its own overlay
        // outside this dialog's DOM subtree. MUI's default focus trap would
        // swallow keystrokes meant for the Link input — turn it off so the
        // OTP field actually receives typed characters.
        disableEnforceFocus
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        {/* Header — hidden in the success state so the post-pay panel
            stands on its own, full-bleed, without a misleading lock-
            icon title row above the green check. */}
        {!done && (
          <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Box
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: '#eef2ff',
                    color: '#4338ca',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LockIcon style={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    style={{ fontWeight: 700, fontSize: 16 }}
                  >
                    {mode === 'save_card'
                      ? 'Save Payment Method'
                      : 'Pay & Activate'}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#6b7280' }}>
                    {mode === 'save_card'
                      ? 'Securely save a card for auto-renewal'
                      : 'Pay securely with card, Apple Pay, or Google Pay'}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => onClose && onClose()}
                disabled={
                  this.state.submitting || this.state.awaitingActivation
                }
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
        )}

        <DialogContent dividers={!done}>
          {loading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={4}
            >
              <CircularProgress size={28} />
              <Typography style={{ marginLeft: 12, color: '#6b7280' }}>
                Preparing secure payment…
              </Typography>
            </Box>
          )}

          {!loading && error && !clientSecret && (
            <Box
              display="flex"
              alignItems="center"
              py={3}
              style={{ color: '#b91c1c' }}
            >
              <ErrorOutlineIcon style={{ marginRight: 8 }} />
              <Typography>{error}</Typography>
            </Box>
          )}

          {!loading && done && (
            <>
              <style>{SUCCESS_KEYFRAMES}</style>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '28px 16px 12px',
                }}
              >
                {/* Animated check bubble with pulsing radial halo. */}
                <Box
                  style={{
                    position: 'relative',
                    width: 92,
                    height: 92,
                    marginBottom: 22,
                  }}
                >
                  <Box
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      background:
                        'radial-gradient(circle, rgba(16,185,129,0.22), transparent 70%)',
                      animation:
                        'nubeSuccessRipple 1.8s ease-out 0.35s infinite',
                    }}
                  />
                  <Box
                    style={{
                      position: 'absolute',
                      inset: 10,
                      borderRadius: '50%',
                      background:
                        'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow:
                        '0 14px 30px -10px rgba(16,185,129,0.55), 0 4px 10px rgba(0,0,0,0.05)',
                      animation:
                        'nubeSuccessPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    }}
                  >
                    <CheckIcon style={{ fontSize: 44, color: '#fff' }} />
                  </Box>
                </Box>

                <Typography
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: '#0f172a',
                    letterSpacing: '-0.02em',
                    animation: 'nubeSuccessSlideUp 0.4s ease-out 0.15s both',
                  }}
                >
                  {mode === 'save_card' ? 'Card Saved' : 'Payment Successful'}
                </Typography>

                {mode !== 'save_card' && this.state.amountUSD != null && (
                  <Typography
                    style={{
                      fontSize: 30,
                      fontWeight: 800,
                      color: '#15803d',
                      letterSpacing: '-0.03em',
                      marginTop: 4,
                      animation: 'nubeSuccessSlideUp 0.4s ease-out 0.22s both',
                    }}
                  >
                    ${Number(this.state.amountUSD).toFixed(2)}
                  </Typography>
                )}

                <Typography
                  style={{
                    fontSize: 13,
                    color: error ? '#92400e' : '#6b7280',
                    textAlign: 'center',
                    lineHeight: 1.6,
                    maxWidth: 300,
                    marginTop: 10,
                    animation: 'nubeSuccessSlideUp 0.4s ease-out 0.3s both',
                  }}
                >
                  {error
                    ? error
                    : mode === 'save_card'
                    ? 'Your card is now on file for future payments.'
                    : 'Your subscription has been updated.'}
                </Typography>
              </Box>
            </>
          )}

          {!loading &&
            !done &&
            mode !== 'save_card' &&
            this.state.totalUSD != null &&
            this.renderPaymentSummary()}

          {!loading && !done && clientSecret && stripePromise && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: { theme: 'stripe' },
              }}
            >
              <PaymentForm
                mode={mode}
                amountUSD={this.state.amountUSD}
                paymentIntentId={this.state.paymentIntentId}
                serialKey={this.props.serialKey}
                serverError={error}
                submitting={this.state.submitting}
                awaitingActivation={awaitingActivation}
                onSubmitStart={() =>
                  this.setSafe({ submitting: true, error: null })
                }
                onSubmitError={(message) =>
                  this.setSafe({ submitting: false, error: message })
                }
                onSubmitSucceeded={() => {
                  // save_card mode runs a SetupIntent — Stripe attaches the
                  // PaymentMethod synchronously, there's no webhook-driven
                  // LicensePayment row to wait on. Skip the activation poll
                  // entirely; finish the flow inline.
                  if (mode === 'save_card') {
                    this.setSafe({ submitting: false, done: true });
                    if (this.props.onSuccess) {
                      this.props.onSuccess({
                        setupIntentId: this.state.setupIntentId,
                        companyId: this.state.companyId,
                      });
                    }
                    return;
                  }
                  // Pay-now flow: flip to awaiting state; polling will close
                  // the loop once the webhook lands and credits the row.
                  this.setSafe({ submitting: false, awaitingActivation: true });
                  this.startActivationPoll();
                }}
              />
            </Elements>
          )}
        </DialogContent>

        <DialogActions
          style={{
            padding: done ? '16px 22px 22px' : '12px 16px',
            justifyContent: done ? 'center' : 'flex-end',
          }}
        >
          {!done && (
            <Typography
              variant="caption"
              style={{ color: '#9ca3af', marginRight: 'auto' }}
            >
              <span role="img" aria-label="lock">
                🔒
              </span>{' '}
              Secured by Stripe
            </Typography>
          )}
          {done ? (
            <Button
              onClick={() => onClose && onClose()}
              variant="contained"
              disableElevation
              fullWidth
              style={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 14,
                borderRadius: 12,
                padding: '11px 24px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                boxShadow: '0 6px 16px -4px rgba(16,185,129,0.5)',
                letterSpacing: '0.01em',
                animation: 'nubeSuccessSlideUp 0.4s ease-out 0.4s both',
              }}
            >
              Close
            </Button>
          ) : (
            <Button
              onClick={() => onClose && onClose()}
              disabled={this.state.submitting || this.state.awaitingActivation}
              size="small"
            >
              Cancel
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}

/**
 * Inner functional component — needs to live inside <Elements> to use the
 * useStripe / useElements hooks. The single Pay button hard-guards against
 * repeat submissions via the `submitting` prop AND a synchronous local
 * ref check so even rapid double-clicks within the same render frame can
 * only fire one confirmPayment.
 */
function PaymentForm({
  mode,
  amountUSD,
  paymentIntentId,
  serialKey,
  serverError,
  submitting,
  awaitingActivation,
  onSubmitStart,
  onSubmitError,
  onSubmitSucceeded,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const inFlight = React.useRef(false);
  const [localError, setLocalError] = React.useState(null);
  // Opt-in save for the pay flow. The PaymentIntent is created without
  // setup_future_usage; we promote it to 'off_session' here at confirm
  // time only when the user ticks the box. save_card mode is a SetupIntent
  // (always saves by design) so the checkbox is hidden there.
  const [saveForFuture, setSaveForFuture] = React.useState(false);

  React.useEffect(() => {
    // Server-side errors override local validation messages so the user
    // always sees the most recent failure.
    if (serverError) setLocalError(serverError);
  }, [serverError]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    // Three guards: button disabled, inFlight ref, submitting prop. The
    // ref catches the synchronous repeat-click case where setState hasn't
    // flushed yet.
    if (inFlight.current) return;
    if (submitting || awaitingActivation) return;
    if (!stripe || !elements) return;

    inFlight.current = true;
    setLocalError(null);
    if (onSubmitStart) onSubmitStart();

    try {
      const isSaveCard = mode === 'save_card';
      const confirmFn = isSaveCard
        ? stripe.confirmSetup
        : stripe.confirmPayment;

      // Sync save-card preference to the PaymentIntent on the server
      // BEFORE confirm. Stripe.js refuses setup_future_usage in
      // confirmParams ("do not pass setup_future_usage at confirm time
      // ... pass it when the PaymentIntent is created"). Since the PI
      // is created when the dialog opens — before the user has seen
      // the checkbox — we update it on-demand via the secret key, then
      // call confirmPayment with no setup_future_usage hint. Only fires
      // for the pay flow; save_card mode is a SetupIntent and always
      // saves by design.
      if (!isSaveCard && paymentIntentId) {
        const syncRes = await AuthUtil.postRequest(
          STRIPE_ENDPOINTS.UPDATE_SAVE_PREFERENCE,
          {
            paymentIntentId,
            saveForFuture,
            serialKey,
          }
        );
        if (!syncRes?.data?.success) {
          const msg =
            syncRes?.data?.message ||
            'Could not update card save preference. Please try again.';
          setLocalError(msg);
          if (onSubmitError) onSubmitError(msg);
          return;
        }
      }

      const result = await confirmFn({
        elements,
        confirmParams: {
          // No return_url means Stripe won't redirect for 3DS unless
          // absolutely required. `redirect: 'if_required'` keeps the
          // flow in-page for the typical case.
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (result?.error) {
        // Card declined, network issue, validation, 3DS canceled — all
        // surface here. Backend never wrote anything (LicensePayment is
        // only created on webhook), so we just let the user retry.
        const message =
          result.error.message ||
          (result.error.code === 'card_declined'
            ? 'Your card was declined.'
            : 'Payment failed. Please try again.');
        setLocalError(message);
        if (onSubmitError) onSubmitError(message);
        return;
      }
      // success — either result.paymentIntent / result.setupIntent will be set
      if (onSubmitSucceeded) onSubmitSucceeded();
    } catch (e) {
      // confirmPayment normally returns {error: ...} for declines /
      // validation / network failures, but a malformed confirmParams or
      // some unexpected Stripe.js internal error can reject the Promise
      // synchronously. Surface the actual error to the user AND console
      // — the previous hardcoded "Network error..." string buried the
      // real cause (e.g. setup_future_usage rejected when combined with
      // automatic_payment_methods.allow_redirects=never on the PI, or
      // PaymentElement validation failures).
      // eslint-disable-next-line no-console
      console.error('[Stripe] confirmPayment threw:', e);
      const message = e?.message
        ? `Payment failed: ${e.message}`
        : 'Payment failed. Please try again.';
      setLocalError(message);
      if (onSubmitError) onSubmitError(message);
    } finally {
      inFlight.current = false;
    }
  };

  const buttonLabel = (() => {
    if (awaitingActivation) return 'Activating…';
    if (submitting) return 'Processing…';
    if (mode === 'save_card') return 'Save Card';
    return amountUSD != null ? `Pay $${Number(amountUSD).toFixed(2)}` : 'Pay';
  })();

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />

      {mode !== 'save_card' && (
        <Box mt={1.5}>
          <FormControlLabel
            control={
              <Checkbox
                checked={saveForFuture}
                onChange={(e) => setSaveForFuture(e.target.checked)}
                disabled={submitting || awaitingActivation}
                size="small"
                color="primary"
              />
            }
            label={
              <Typography style={{ fontSize: 13, color: '#374151' }}>
                Save this card for future payments
              </Typography>
            }
          />
        </Box>
      )}

      {localError && (
        <Box
          mt={2}
          p={1.5}
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            color: '#b91c1c',
            fontSize: 13,
          }}
        >
          <ErrorOutlineIcon style={{ marginRight: 8, fontSize: 18 }} />
          {localError}
        </Box>
      )}

      <Box mt={2}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={!stripe || !elements || submitting || awaitingActivation}
          style={{ height: 44, fontWeight: 700, textTransform: 'none' }}
          startIcon={
            submitting || awaitingActivation ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {buttonLabel}
        </Button>
      </Box>
    </form>
  );
}

export default PayWithStripeDialog;
